import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { X } from 'lucide-react';
import legacyUapiService from '../services/LegacyUapiService';
import uapiIntegrationsService from '../services/UapiIntegrationsService';
import { apiErrorMessage } from '../utils/apiError';
import { formatDateTime } from '../utils/datetime';
import CustomCodeAssistant from './CustomCodeAssistant';
import UapiHookTester, { defaultHookTestContext } from './shared/UapiHookTester';
import ConfirmModal from './shared/ConfirmModal';
import Spinner from './shared/Spinner';
import { useAccount } from '../contexts/AccountContext';
import './PartnerSyncTemplates.css';
import './IntegrationRouteCodePage.css';
import './UniversalApiPage.css';

// ONE UNIVERSAL API CUSTOM CODE SNIPPET (V528) — per-client code around a backend system's integration.
//
//     request ──▶ before(ctx) ──▶ integration (this.custom = ctx.custom) ──▶ backend
//     response ◀── after(ctx) ◀──────────────────────────────────────────────┘
//
// Opened from a hotel's backend binding (?accessId= — a new snippet is attached to it on save, and Back
// returns there) or from the Custom Code tab. THE SAME EDITOR PIECES as every other custom code screen —
// CustomCodeAssistant for Claude, the pst-* layout — so the screens read alike; the contract differs, so
// the tester is the hook tester rather than the sync one.
//
// A FULL SCREEN, NOT A MODAL (STYLE.md): code is edited for longer than a dialog is meant to stay open.

const skeleton = (systemKey, systemName) => {
    const azure = systemKey === 'AZUREBLOB';
    return `// Custom code for ${systemName || 'this backend'} — runs on every call to it for the
// backends it is attached to. Either hook may be removed.
module.exports = {
    // BEFORE the backend is called. Change ctx.request in place, or set ctx.custom.* for the
    // integration to read as this.custom. Return ctx.respond(status, body) to answer without calling it.
    // Read this binding's non-AUTH Settings with ctx.get('NAME') (case-insensitive) or ctx.settings.
    async before(ctx) {
${azure ? `        // WHERE THIS CLIENT'S FILES GO. The Azure integration is generic — it stores at exactly the
        // name set here and derives none of its own; with none set, the call is refused.
        //   blobName    the file a read / write / merge / delete addresses
        //   blobPrefix  the folder a GET lists when there is no file at blobName
        //   id          handed back on a POST, so the caller can address the record again
        // Build names from ctx.propertyCode, never ctx.pathParams.hotelId (that is the caller's value).
        var folder = 'inbound/' + ctx.propertyCode;
        var id = ctx.pathParams.reservationId || ctx.transactionId;   // a POST has no id yet
        ctx.custom.blobPrefix = folder + '/';
        if (ctx.pathParams.reservationId || ctx.request.method !== 'GET') {
            ctx.custom.blobName = folder + '/RES_' + id + '.json';
            if (!ctx.pathParams.reservationId) ctx.custom.id = id;
        }
        ctx.log('blob', ctx.custom.blobName || ctx.custom.blobPrefix);
` : `        // e.g. ctx.request.body = … ; ctx.custom.someOption = …;
`}    },

    // AFTER the backend answered. ctx.response = { statusCode, body, headers } — change it in place,
    // or return a replacement. The gateway's redaction still runs after this.
    async after(ctx) {
    },
};
`;
};

const SUGGESTIONS = [
    'Explain what this snippet does',
    'Name the Azure blob <property>/<yyyy-mm-dd>/<reservation id>.json',
    'Refuse DELETE requests for this client with a 403',
];

const UapiCustomCodePage = () => {
    const { id } = useParams();
    const isNew = !id || id === 'new';
    const navigate = useNavigate();
    const location = useLocation();
    const accessId = new URLSearchParams(location.search).get('accessId');
    // Or opened from a pipeline template's route (V533) — the Integration column's Custom Code button.
    const routeId = new URLSearchParams(location.search).get('routeId');
    // WHOSE CODE IT IS (HIL-154, V702): a chain, or one hotel of it. A new snippet takes the navbar's chain — or, opened
    // from a binding or a template route, that one's chain (the server reads it from there).
    const { selectedAccount, accounts } = useAccount();
    const navbarChain = (accounts || []).find(a => String(a.id) === String(selectedAccount)) || null;

    const [snippet, setSnippet] = useState(null);           // as saved
    const [form, setForm] = useState({ name: '', description: '', code: '', active: 'N', marketplaceId: '', moduleIds: [] });
    // Every Universal API module (top-level route) a snippet can be targeted at, with the systems serving it.
    const [modules, setModules] = useState([]);
    const [binding, setBinding] = useState(null);           // the binding this screen was opened from
    const [route, setRoute] = useState(null);               // …or the template route
    const [systems, setSystems] = useState([]);             // for a new snippet opened from the tab
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [detaching, setDetaching] = useState(null);
    const [testOpen, setTestOpen] = useState(false);
    const [assistOpen, setAssistOpen] = useState(true);
    const [testContext, setTestContext] = useState('');
    const nameRef = useRef(null);

    // Back and Save return to the page this was opened from: the binding or the template route when there
    // is one, else the tab.
    const parentPath = binding
        ? `/universal-api/authentication/${binding.hotel_id}/access/${binding.id}`
        : route
            ? `/pipeline-templates/${route.integration_id}/routes/${route.id}/integration-code`
            : '/universal-api/custom-code';

    const load = useCallback(async () => {
        setLoadError(null);
        try {
            const [snippetRes, accessRes, routeRes, routesRes] = await Promise.all([
                isNew ? null : legacyUapiService.getCustomCode(id),
                accessId ? legacyUapiService.getAccess(accessId) : null,
                !accessId && routeId ? legacyUapiService.listRouteCustomCode(routeId) : null,
                legacyUapiService.listRoutes(),
            ]);
            setBinding(accessRes ? (accessRes.data || accessRes) : null);
            const rt = routeRes ? ((routeRes.data || routeRes).target || null) : null;
            setRoute(rt);
            // What a new snippet is for: the binding's system, or the system the template route targets.
            const b = accessRes ? (accessRes.data || accessRes) : (rt && rt.marketplace_id ? rt : null);
            // Modules are the top-level routes. /oauth is the token endpoint — never forwarded to a backend,
            // so nothing could run there.
            const roots = ((routesRes.data || routesRes).routes || [])
                .filter(r => !r.parent_id && r.path !== '/oauth')
                .map(r => ({ id: String(r.id), path: r.path, systems: String(r.systems || '').split('|').filter(Boolean) }));
            setModules(roots);
            if (snippetRes) {
                const s = snippetRes.data || snippetRes;
                setSnippet(s);
                setForm({
                    name: s.name || '', description: s.description || '', code: s.code || '',
                    active: s.active === 'Y' ? 'Y' : 'N', marketplaceId: String(s.marketplace_id),
                    moduleIds: (s.modules || []).map(m => String(m.id)),
                });
                const first = (s.attachments || [])[0];
                setTestContext(defaultHookTestContext({ propertyCode: first && first.hotelid_override }));
            } else {
                // Opened from a binding: start targeted at the modules that binding's system serves — the
                // usual answer, and visibly ticked so it is a choice rather than a default nobody saw.
                setForm(f => ({
                    ...f,
                    marketplaceId: b ? String(b.marketplace_id) : '',
                    moduleIds: b ? roots.filter(r => r.systems.includes(b.system_name)).map(r => r.id) : [],
                }));
                setTestContext(defaultHookTestContext({ propertyCode: b && b.hotelid_override }));
                if (!b) {
                    // Every system the Universal API can reach: those with an integration and those without.
                    const res = await uapiIntegrationsService.list();
                    const d = res.data || res;
                    const all = [
                        ...(d.integrations || []).map(i => ({ id: i.marketplace_id, name: i.system_name, key: i.system_key })),
                        ...(d.availableSystems || []).map(m => ({ id: m.marketplace_id, name: m.system_name, key: m.system_key })),
                    ].filter(x => x.id);
                    setSystems(all.sort((a, b2) => String(a.name).localeCompare(String(b2.name))));
                }
            }
        } catch (e) {
            setLoadError(apiErrorMessage(e, 'This custom code could not be loaded.'));
        } finally {
            setLoading(false);
        }
    }, [id, isNew, accessId, routeId]);

    useEffect(() => { load(); }, [load]);

    const system = snippet
        ? { id: snippet.marketplace_id, name: snippet.system_name, key: snippet.system_key }
        : binding
            ? { id: binding.marketplace_id, name: binding.system_name, key: binding.system_key }
            : route && route.marketplace_id
                ? { id: route.marketplace_id, name: route.system_name, key: route.system_key }
                : systems.find(s => String(s.id) === String(form.marketplaceId)) || null;

    const set = (patch) => { setSaveError(null); setForm(f => ({ ...f, ...patch })); };

    const toggleModule = (moduleId) => set({
        moduleIds: form.moduleIds.includes(moduleId)
            ? form.moduleIds.filter(x => x !== moduleId)
            : [...form.moduleIds, moduleId],
    });

    // The system's own modules first — the ones its integration answers — then the rest.
    const servedBySystem = (m) => !!(system && m.systems.includes(system.name));
    const orderedModules = [...modules].sort((a, b2) =>
        (servedBySystem(b2) - servedBySystem(a)) || a.path.localeCompare(b2.path));

    const save = async () => {
        // A disabled Save explains nothing — accept the click and name what is missing.
        if (!form.name.trim()) {
            setSaveError('Give the custom code a name before saving.');
            if (nameRef.current) nameRef.current.focus();
            return;
        }
        if (!form.marketplaceId) { setSaveError('Choose the backend system this code is for.'); return; }
        if (isNew && !binding && !route && !selectedAccount) {
            setSaveError('Choose the chain this code is for in the navbar first — custom code belongs to one client.');
            return;
        }
        if (form.active === 'Y' && !form.moduleIds.length) {
            setSaveError('Choose at least one module — active custom code runs only on the modules it targets.');
            return;
        }
        setSaving(true);
        setSaveError(null);
        try {
            if (isNew) {
                // THE SERVER CREATES IT INACTIVE whatever is sent. Switching it on is its own update, which
                // is also where the server refuses code that does not compile.
                const created = await legacyUapiService.createCustomCode({
                    name: form.name.trim(),
                    description: form.description || null,
                    code: form.code,
                    marketplaceId: Number(form.marketplaceId),
                    accessId: binding ? binding.id : undefined,
                    routeId: !binding && route ? route.id : undefined,
                    // From a binding or route the server takes ITS chain; from the tab, the navbar's.
                    accountId: !binding && !route && selectedAccount ? Number(selectedAccount) : undefined,
                    moduleIds: form.moduleIds.map(Number),
                });
                const c = created.data || created;
                if (form.active === 'Y') await legacyUapiService.updateCustomCode(c.id, { active: 'Y' });
            } else {
                // Only what changed — the server writes exactly the keys it receives.
                const patch = {};
                if (form.name.trim() !== snippet.name) patch.name = form.name.trim();
                if ((form.description || '') !== (snippet.description || '')) patch.description = form.description || null;
                if ((form.code || '') !== (snippet.code || '')) patch.code = form.code;
                if (form.active !== (snippet.active === 'Y' ? 'Y' : 'N')) patch.active = form.active;
                const savedModules = (snippet.modules || []).map(m => String(m.id)).sort().join(',');
                if ([...form.moduleIds].sort().join(',') !== savedModules) patch.moduleIds = form.moduleIds.map(Number);
                if (Object.keys(patch).length) await legacyUapiService.updateCustomCode(snippet.id, patch);
            }
            navigate(parentPath);
        } catch (e) {
            setSaveError(apiErrorMessage(e, 'The custom code could not be saved.'));
            setSaving(false);
        }
    };

    const remove = async () => {
        setConfirmDelete(false);
        setSaving(true);
        try {
            await legacyUapiService.deleteCustomCode(snippet.id);
            navigate(parentPath);
        } catch (e) {
            setSaveError(apiErrorMessage(e, 'The custom code could not be deleted.'));
            setSaving(false);
        }
    };

    // From a binding (a.access_id) or a template route (a.route_id) — the same detach, on its own table.
    const detach = async (a) => {
        const key = a.route_id ? `route-${a.route_id}` : `access-${a.access_id}`;
        setDetaching(key);
        try {
            if (a.route_id) {
                await legacyUapiService.detachRouteCustomCode(a.route_id, snippet.id);
                setSnippet(s => ({ ...s, routeAttachments: s.routeAttachments.filter(x => x.route_id !== a.route_id) }));
            } else {
                await legacyUapiService.detachAccessCustomCode(a.access_id, snippet.id);
                setSnippet(s => ({ ...s, attachments: s.attachments.filter(x => x.access_id !== a.access_id) }));
            }
        } catch (e) {
            setSaveError(apiErrorMessage(e, a.route_id ? 'Could not detach it from that route.' : 'Could not detach it from that backend.'));
        } finally {
            setDetaching(null);
        }
    };

    if (loading) return <div className="pst-container"><Spinner /></div>;

    const header = (
        <div className="pst-detail-header pst-detail-header-stacked">
            <button className="pst-back-btn" onClick={() => navigate(parentPath)} disabled={saving}>
                ← {binding ? `Back to ${binding.label || binding.system_name} for ${binding.hotelname}`
                    : route ? `Back to ${route.template_name}` : 'Back to Custom Code'}
            </button>
            <div className="pst-detail-header-main">
                <div>
                    <div className="pst-detail-title">{isNew ? 'Add Custom Code' : (snippet && snippet.name)}</div>
                    <div className="irc-route">
                        <span className="irc-hop">request</span>
                        <span className="irc-arrow" aria-hidden="true">→</span>
                        <span className="irc-mods"><code>before()</code></span>
                        <span className="irc-arrow" aria-hidden="true">→</span>
                        <span className="irc-hop">{system ? system.name : 'backend system'}</span>
                        <span className="irc-arrow" aria-hidden="true">→</span>
                        <span className="irc-mods"><code>after()</code></span>
                        <span className="irc-arrow" aria-hidden="true">→</span>
                        <span className="irc-hop">response</span>
                    </div>
                </div>
            </div>
        </div>
    );

    if (loadError) {
        return (
            <div className="pst-container">
                {header}
                <div className="irc-empty">{loadError}</div>
            </div>
        );
    }

    const attachments = (snippet && snippet.attachments) || [];
    const routeAttachments = (snippet && snippet.routeAttachments) || [];

    return (
        <div className="pst-container">
            {header}

            <div className="pst-code-screen">
                <div className="pst-form-row">
                    <div className="pst-form-group">
                        <label htmlFor="ucc-name">Name *</label>
                        <input
                            id="ucc-name"
                            ref={nameRef}
                            value={form.name}
                            onChange={e => set({ name: e.target.value })}
                            placeholder="e.g. Bolton blob naming"
                            autoFocus={isNew}
                        />
                    </div>
                    <div className="pst-form-group">
                        <label htmlFor="ucc-system">Backend system *</label>
                        {/* Fixed once chosen: the code is written against that system's integration, and the
                            this.custom keys it sets mean nothing to another one. */}
                        {isNew && !binding ? (
                            <select
                                id="ucc-system"
                                value={form.marketplaceId}
                                onChange={e => set({ marketplaceId: e.target.value })}
                            >
                                <option value="">Choose a system…</option>
                                {systems.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                            </select>
                        ) : (
                            <div><span className="uapi-system-chip">{system && system.name}</span></div>
                        )}
                    </div>
                    <div className="pst-form-group">
                        <label>Belongs to</label>
                        {isNew ? (
                            <div>{binding || route
                                ? <span className="lu-muted">the chain of the {binding ? 'hotel' : 'template'} it is opened from</span>
                                : navbarChain ? <strong>{navbarChain.name}</strong>
                                    : <span className="lu-muted">choose a chain in the navbar</span>}</div>
                        ) : snippet && snippet.owner_chain_name ? (
                            <div>
                                <strong>{snippet.owner_chain_name}</strong>
                                {snippet.owner_hotel_name && <span className="lu-muted"> — {snippet.owner_hotel_name} only</span>}
                            </div>
                        ) : (
                            // Code from before owners (V702) that runs nowhere, or across chains, has none until given one.
                            <div>
                                <span className="lu-muted">No chain</span>
                                {navbarChain && (
                                    <button type="button" className="pst-active-toggle" style={{ marginLeft: 8 }}
                                        onClick={async () => {
                                            try {
                                                await legacyUapiService.updateCustomCode(snippet.id, { accountId: Number(selectedAccount) });
                                                setSnippet(prev => ({ ...prev, account_id: Number(selectedAccount), owner_chain_id: Number(selectedAccount),
                                                    owner_chain_name: navbarChain.name }));
                                            } catch (e) { setSaveError(apiErrorMessage(e, 'The chain could not be set.')); }
                                        }}>
                                        Give it to {navbarChain.name}
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="pst-form-group">
                        <label>Status</label>
                        <button
                            type="button"
                            className={`pst-active-toggle ${form.active === 'Y' ? 'is-on' : 'is-off'}`}
                            aria-pressed={form.active === 'Y'}
                            onClick={() => set({ active: form.active === 'Y' ? 'N' : 'Y' })}
                        >
                            <span className="pst-active-dot" />
                            {form.active === 'Y' ? 'Active — runs on its backends' : 'Inactive — not run'}
                        </button>
                    </div>
                </div>

                <div className="pst-form-group">
                    <label htmlFor="ucc-description">Description</label>
                    <input
                        id="ucc-description"
                        value={form.description}
                        onChange={e => set({ description: e.target.value })}
                        placeholder="What this changes for the client, and why"
                    />
                </div>

                {/* WHICH MODULES (V529). A snippet runs only on calls to the modules ticked here — a blob-naming
                    rule for /rsv must not rename what /crm writes. None ticked means it runs nowhere, which
                    is why an active snippet needs at least one. */}
                <fieldset className="pst-form-group irc-routes">
                    <legend>Modules</legend>
                    {orderedModules.length === 0 ? (
                        <span className="lu-muted">No Universal API modules are defined yet.</span>
                    ) : (
                        <div className="uapi-module-grid">
                            {orderedModules.map(m => (
                                <label key={m.id} className="uapi-module-check">
                                    <input
                                        type="checkbox"
                                        checked={form.moduleIds.includes(m.id)}
                                        onChange={() => toggleModule(m.id)}
                                    />
                                    {m.path}
                                    {system && !servedBySystem(m) && (
                                        <span className="lu-muted uapi-cc-module-note"> — not served by {system.name}</span>
                                    )}
                                </label>
                            ))}
                        </div>
                    )}
                    {form.moduleIds.length === 0 && orderedModules.length > 0 && (
                        <p className="ppw-error-msg" style={{ margin: '0.5rem 0 0' }}>
                            No module chosen — this code will not run on any call.
                        </p>
                    )}
                </fieldset>

                {/* WHERE IT RUNS. Attached from each backend's own page; detached here or there. */}
                {!isNew && (
                    <fieldset className="pst-form-group irc-routes">
                        <legend>Runs on</legend>
                        {attachments.length === 0 && routeAttachments.length === 0 && (
                            <span className="lu-muted">
                                Not attached anywhere yet — open a hotel's {system && system.name} backend under
                                Authentication and Access, or a pipeline template route's Integration custom code,
                                and add it there.
                            </span>
                        )}
                        {/* PIPELINE TEMPLATE ROUTES (V533) — runs for every property implementing the template,
                            ahead of anything attached to that property's own backend. */}
                        {routeAttachments.map(a => (
                            <div key={`route-${a.route_id}`} className="irc-route-check">
                                <span>
                                    <Link to={`/pipeline-templates/${a.integration_id}/routes/${a.route_id}/integration-code`}>
                                        {a.template_name}{a.route_name ? ` · ${a.route_name}` : ''}
                                    </Link>{' '}
                                    <span className="lu-muted">pipeline template — every property running it</span>
                                </span>
                                {route && String(route.id) === String(a.route_id) && <span className="irc-this">this route</span>}
                                <button
                                    type="button"
                                    className="uapi-module-chip-remove"
                                    title={`Stop running this on ${a.template_name}`}
                                    onClick={() => detach(a)}
                                    disabled={detaching === `route-${a.route_id}`}
                                >
                                    <X size={12} />
                                </button>
                            </div>
                        ))}
                        {attachments.map(a => (
                            <div key={a.access_id} className="irc-route-check">
                                <span>
                                    <Link to={`/universal-api/authentication/${a.hotel_id}/access/${a.access_id}`}>
                                        {a.hotelname}{a.label ? ` · ${a.label}` : ''}
                                    </Link>{' '}
                                    <code className="uapi-xhotel">{a.uapi_hotel_id}</code>
                                </span>
                                {binding && String(binding.id) === String(a.access_id) && <span className="irc-this">this backend</span>}
                                <button
                                    type="button"
                                    className="uapi-module-chip-remove"
                                    title={`Stop running this on ${a.hotelname}`}
                                    onClick={() => detach(a)}
                                    disabled={detaching === `access-${a.access_id}`}
                                >
                                    <X size={12} />
                                </button>
                            </div>
                        ))}
                    </fieldset>
                )}
                {isNew && binding && (
                    <p className="lu-flow-note">
                        Saved, this is attached to {binding.label || binding.system_name} for {binding.hotelname}.
                    </p>
                )}
                {isNew && !binding && route && (
                    <p className="lu-flow-note">
                        Saved, this is attached to {route.route_name || 'this route'} on {route.template_name}, and
                        runs for every property implementing it.
                    </p>
                )}

                <div className="pst-form-group">
                    <div className="irc-code-head">
                        <label htmlFor="ucc-code">Code</label>
                        <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => set({ code: skeleton(system && system.key, system && system.name) })}
                            disabled={!!(form.code && form.code.trim())}
                            title={form.code && form.code.trim() ? 'Clear the editor first to start from the example' : 'Start from an example'}
                        >
                            Initialize
                        </button>
                    </div>
                    <textarea
                        id="ucc-code"
                        className="pst-code-editor"
                        value={form.code}
                        onChange={e => set({ code: e.target.value })}
                        spellCheck={false}
                        rows={22}
                        placeholder="// module.exports = { async before(ctx) { … }, async after(ctx) { … } } — click Initialize for an example"
                    />
                    {/* The server's own compile, with the gateway's loader — shown as saved. */}
                    {snippet && snippet.compileError && form.code === snippet.code && (
                        <p className="ppw-error-msg" style={{ marginTop: '0.4rem' }}>
                            Does not compile: {snippet.compileError}
                        </p>
                    )}
                </div>

                <div className="pst-test">
                    <button type="button" className="pst-test-toggle" onClick={() => setTestOpen(!testOpen)}>
                        <span className={`pst-test-chevron ${testOpen ? 'open' : ''}`}>&#9656;</span>
                        Test the hooks
                    </button>
                    {testOpen && (
                        <UapiHookTester code={form.code} context={testContext} onContextChange={setTestContext} />
                    )}
                </div>

                <div className="pst-test">
                    <button type="button" className="pst-test-toggle" onClick={() => setAssistOpen(!assistOpen)}>
                        <span className={`pst-test-chevron ${assistOpen ? 'open' : ''}`}>&#9656;</span>
                        Develop with Claude
                    </button>
                    {assistOpen && (
                        <div className="pst-test-body">
                            {form.marketplaceId ? (
                                <CustomCodeAssistant
                                    assist={legacyUapiService.assistCustomCode}
                                    suggestions={SUGGESTIONS}
                                    note={`knows the hook contract, the ${system ? system.name : ''} integration and your current code`}
                                    snippetContext={{
                                        id: snippet ? snippet.id : null,
                                        marketplaceId: Number(form.marketplaceId),
                                        modules: modules.filter(m => form.moduleIds.includes(m.id)).map(m => m.path),
                                        code: form.code || '',
                                    }}
                                    onInsert={(code) => set({ code })}
                                />
                            ) : (
                                <span className="lu-muted">Choose the backend system first — Claude needs its integration.</span>
                            )}
                        </div>
                    )}
                </div>

                <div className="pst-modal-actions">
                    {saveError && <div className="pst-save-error" role="alert">{saveError}</div>}
                    {!isNew && (
                        <button type="button" className="btn btn-secondary irc-delete" onClick={() => setConfirmDelete(true)} disabled={saving}>
                            Delete
                        </button>
                    )}
                    {snippet && <span className="lu-muted uapi-cc-updated">Updated {formatDateTime(snippet.updated_at)}</span>}
                    <button type="button" className="btn btn-secondary" onClick={() => navigate(parentPath)} disabled={saving}>Cancel</button>
                    <button type="button" className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
                </div>
            </div>

            {confirmDelete && (
                <ConfirmModal
                    title="Delete this custom code?"
                    message={`"${snippet.name}" will stop running on every backend it is attached to`
                        + `${attachments.length ? ` (${attachments.length})` : ''}. This cannot be undone.`}
                    confirmLabel="Delete"
                    danger
                    onConfirm={remove}
                    onCancel={() => setConfirmDelete(false)}
                />
            )}
        </div>
    );
};

export default UapiCustomCodePage;
